export interface FounderProfile {
  role: string;
  experience: string[];
  knowledge: string[];
  personality: string[];
  likes: string[];
  dislikes: string[];
  nonNegotiables: string[];
  availableCapital: string | null;
  timeCommitment: string | null;
  geographicPreference: string | null;
  completedAt: string | null;
}

export interface LocationSettings {
  geoConsent: boolean;
  locationMode: 'auto' | 'manual';
  manualLocation: string;
  detectedLocation: string;
  businessFocus: 'local' | 'regional' | 'national' | 'global' | 'flexible';
}

export interface AccountMetadata {
  email: string | null;
  createdAt: string | null;
}

interface FounderProfilePayload {
  hasProfile: boolean;
  profile: FounderProfile | null;
}

const LOCATION_MODES = new Set<LocationSettings['locationMode']>(['auto', 'manual']);
const LOCATION_FOCUSES = new Set<LocationSettings['businessFocus']>([
  'local',
  'regional',
  'national',
  'global',
  'flexible',
]);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isNullableString = (value: unknown): value is string | null => (
  value === null || typeof value === 'string'
);

const isStringArray = (value: unknown): value is string[] => (
  Array.isArray(value) && value.every((item) => typeof item === 'string')
);

export const parseLocationSettings = (value: unknown): LocationSettings | null => {
  if (!isRecord(value)) return null;
  if (typeof value.geoConsent !== 'boolean') return null;
  if (!LOCATION_MODES.has(value.locationMode as LocationSettings['locationMode'])) return null;
  if (typeof value.manualLocation !== 'string' || typeof value.detectedLocation !== 'string') return null;
  if (!LOCATION_FOCUSES.has(value.businessFocus as LocationSettings['businessFocus'])) return null;

  return {
    geoConsent: value.geoConsent,
    locationMode: value.locationMode as LocationSettings['locationMode'],
    manualLocation: value.manualLocation,
    detectedLocation: value.detectedLocation,
    businessFocus: value.businessFocus as LocationSettings['businessFocus'],
  };
};

export const parseFounderProfilePayload = (value: unknown): FounderProfilePayload | null => {
  if (!isRecord(value) || value.success !== true || typeof value.hasProfile !== 'boolean') return null;
  if (!value.hasProfile) {
    return value.profile === null ? { hasProfile: false, profile: null } : null;
  }

  const profile = value.profile;
  if (!isRecord(profile) || typeof profile.role !== 'string' || profile.role.trim().length === 0) return null;
  if (!isStringArray(profile.experience) || !isStringArray(profile.knowledge)) return null;
  if (!isStringArray(profile.personality) || !isStringArray(profile.likes)) return null;
  if (!isStringArray(profile.dislikes) || !isStringArray(profile.nonNegotiables)) return null;
  if (!isNullableString(profile.availableCapital) || !isNullableString(profile.timeCommitment)) return null;
  if (!isNullableString(profile.geographicPreference) || !isNullableString(profile.completedAt)) return null;

  return {
    hasProfile: true,
    profile: {
      role: profile.role,
      experience: profile.experience,
      knowledge: profile.knowledge,
      personality: profile.personality,
      likes: profile.likes,
      dislikes: profile.dislikes,
      nonNegotiables: profile.nonNegotiables,
      availableCapital: profile.availableCapital,
      timeCommitment: profile.timeCommitment,
      geographicPreference: profile.geographicPreference,
      completedAt: profile.completedAt,
    },
  };
};

export const parseAccountMetadataPayload = (value: unknown): AccountMetadata | null => {
  if (!isRecord(value) || value.success !== true || !isRecord(value.account)) return null;
  if (!isNullableString(value.account.email) || !isNullableString(value.account.createdAt)) return null;
  if (value.account.createdAt && Number.isNaN(Date.parse(value.account.createdAt))) return null;

  return {
    email: value.account.email,
    createdAt: value.account.createdAt,
  };
};
